import { useMemo } from 'react';
import { useFormContext } from '../../../../../contexts/useFormContext';
import {
  usePaymentDetailsSchema,
  useRespLawyerSchema,
  useFirstPartnerSchema,
  useSecondPartnerSchema,
  useDocumentSavingSchema,
  usePaymentConfirmationSchema,
  useReassignmentSchema,
  usePaymentHoldReviewSchema,
  useDubaiPaymentFromOfficeAccountPaymentDetailsSchema,
  useDubaiPaymentFromOfficeAccountApproverSchema,
  useDubaiTransferRequisitionPaymentDetailsSchema,
  useDubaiTransferRequisitionFirstPartnerApprovalSchema,
  useDubaiTransferRequisitionSecondPartnerApprovalSchema,
  useDubaiTransferRequisitionPaymentConfirmationSchema,
  useDubaiPaymentFromOfficeAccountPaymentConfirmationSchema,
  useOvertimeApprovalRequestDetailsSchema,
  useOvertimeApprovalLineManagerSchema,
  useOvertimeApprovalFinanceConfirmationSchema,
  useGiftHospitalityDonationRequestDetailsSchema,
  useGiftHospitalityDonationApprovalSchema,
  useGiftHospitalityDonationDubaiGiftHospitalityDonationDubaiRequestDetailsSchema,
  useGiftHospitalityDonationDubaiGiftHospitalityDonationDubaiApprovalSchema,
  useGiftHospitalityDonationDublinGiftHospitalityDonationDublinRequestDetailsSchema,
  useGiftHospitalityDonationDublinGiftHospitalityDonationDublinApprovalSchema,
  // PLOP_INJECT_SCHEMA_IMPORTS
} from '../../schemas';
import { FormState } from '../../types/core/form-state.types';

/**
 * Shared utility to check if form state is ready for schema computation
 * Can be used by individual schema hooks to prevent unnecessary computation
 */
export const isFormStateReady = (formState: FormState): boolean => {
  return (
    !formState.isLoading &&
    !!formState.data &&
    Object.keys(formState.data).length > 0
  );
};

/**
 * Empty schema placeholder
 */
const EMPTY_SCHEMA = { sections: [] };

/**
 * useSchemas Hook
 *
 * A custom React hook that manages and consolidates form validation schemas
 * for different sections of a multi-step form process.
 *
 * @param {FormState} formState - Current state of the form
 * @param {Function} updateFormData - Callback function to update form data
 * @param {number} schemaVersion - Version number to force schema refresh
 *
 * @returns {Record<string, FormSchemaConfig>} An object containing validation schemas for all form sections
 */
export const useSchemas = () => {
  const { formState } = useFormContext();

  // Check if form is ready - don't compute schemas until formState.data is available
  const isFormReady = isFormStateReady(formState);

  // Hooks must be called unconditionally (React requirement)
  // Dubai Payment Approval
  const paymentDetailsSchema = usePaymentDetailsSchema(formState);
  const respLawyerSchema = useRespLawyerSchema(formState);
  const firstPartnerSchema = useFirstPartnerSchema(formState);
  const secondPartnerSchema = useSecondPartnerSchema(formState);
  const paymentHoldReviewSchema = usePaymentHoldReviewSchema(formState);
  const documentSavingSchema = useDocumentSavingSchema(formState);
  const paymentConfirmationSchema = usePaymentConfirmationSchema(formState);
  const reassignmentSchema = useReassignmentSchema(formState);

  // Dubai Payment From Office Account
  const dubaiPaymentFromOfficePaymentDetailsSchema =
    useDubaiPaymentFromOfficeAccountPaymentDetailsSchema(formState);
  const dubaiPaymentFromOfficeApproverSchema =
    useDubaiPaymentFromOfficeAccountApproverSchema(formState);
  const dubaiPaymentFromOfficePaymentConfirmationSchema =
    useDubaiPaymentFromOfficeAccountPaymentConfirmationSchema(formState);

  // Dubai Transfer Requisition
  const dubaiTransferRequisitionPaymentDetailsSchema =
    useDubaiTransferRequisitionPaymentDetailsSchema(formState);
  const dubaiTransferRequisitionFirstPartnerApprovalSchema =
    useDubaiTransferRequisitionFirstPartnerApprovalSchema(formState);
  const dubaiTransferRequisitionSecondPartnerApprovalSchema =
    useDubaiTransferRequisitionSecondPartnerApprovalSchema(formState);
  const dubaiTransferRequisitionPaymentConfirmationSchema =
    useDubaiTransferRequisitionPaymentConfirmationSchema(formState);

  // Overtime Approval
  const overtimeApprovalRequestDetailsSchema =
    useOvertimeApprovalRequestDetailsSchema(formState);
  const overtimeApprovalLineManagerSchema =
    useOvertimeApprovalLineManagerSchema(formState);
  const overtimeApprovalFinanceConfirmationSchema =
    useOvertimeApprovalFinanceConfirmationSchema(formState);
  const giftHospitalityDonationRequestDetailsSchema =
    useGiftHospitalityDonationRequestDetailsSchema(formState);
  const giftHospitalityDonationApprovalSchema =
    useGiftHospitalityDonationApprovalSchema(formState);
  const giftHospitalityDonationDubaiGiftHospitalityDonationDubaiRequestDetailsSchema =
    useGiftHospitalityDonationDubaiGiftHospitalityDonationDubaiRequestDetailsSchema(
      formState,
    );
  const giftHospitalityDonationDubaiGiftHospitalityDonationDubaiApprovalSchema =
    useGiftHospitalityDonationDubaiGiftHospitalityDonationDubaiApprovalSchema(
      formState,
    );
  const giftHospitalityDonationDublinGiftHospitalityDonationDublinRequestDetailsSchema =
    useGiftHospitalityDonationDublinGiftHospitalityDonationDublinRequestDetailsSchema(
      formState,
    );
  const giftHospitalityDonationDublinGiftHospitalityDonationDublinApprovalSchema =
    useGiftHospitalityDonationDublinGiftHospitalityDonationDublinApprovalSchema(
      formState,
    );
  // PLOP_INJECT_SCHEMA_HOOKS

  // Conditionally return schemas based on readiness
  return useMemo(() => {
    // Return empty schemas if form is not ready to prevent unnecessary renders
    if (!isFormReady) {
      return {
        // Dubai Payment Approval
        request_reassign: EMPTY_SCHEMA,
        payment_details: EMPTY_SCHEMA,
        resp_lawyer: EMPTY_SCHEMA,
        first_partner: EMPTY_SCHEMA,
        second_partner: EMPTY_SCHEMA,
        payment_hold_review: EMPTY_SCHEMA,
        payment_confirmation: EMPTY_SCHEMA,
        document_saving: EMPTY_SCHEMA,
        // Dubai Payment From Office Account
        dubai_payment_from_office_account_payment_details: EMPTY_SCHEMA,
        dubai_payment_from_office_account_approver: EMPTY_SCHEMA,
        dubai_payment_from_office_account_payment_confirmation: EMPTY_SCHEMA,
        // Dubai Transfer Requisition
        dubai_transfer_requisition_payment_details: EMPTY_SCHEMA,
        dubai_transfer_requisition_first_partner: EMPTY_SCHEMA,
        dubai_transfer_requisition_second_partner: EMPTY_SCHEMA,
        dubai_transfer_requisition_payment_confirmation: EMPTY_SCHEMA,
        // Overtime Approval
        overtime_approval_request_details: EMPTY_SCHEMA,
        overtime_approval_line_manager: EMPTY_SCHEMA,
        overtime_approval_finance_confirmation: EMPTY_SCHEMA,
        gift_hospitality_donation_uk_request_details: EMPTY_SCHEMA,
        gift_hospitality_donation_uk_approval: EMPTY_SCHEMA,
        approval: EMPTY_SCHEMA,
        gift_hospitality_donation_dubai_request_details: EMPTY_SCHEMA,
        gift_hospitality_donation_dubai_approval: EMPTY_SCHEMA,
        gift_hospitality_donation_dublin_request_details: EMPTY_SCHEMA,
        gift_hospitality_donation_dublin_approval: EMPTY_SCHEMA,
        // PLOP_INJECT_EMPTY_SCHEMAS
      };
    }

    // Return actual schemas when form is ready
    return {
      // Dubai Payment Approval
      request_reassign: reassignmentSchema,
      payment_details: paymentDetailsSchema,
      resp_lawyer: respLawyerSchema,
      first_partner: firstPartnerSchema,
      second_partner: secondPartnerSchema,
      payment_hold_review: paymentHoldReviewSchema,
      payment_confirmation: paymentConfirmationSchema,
      document_saving: documentSavingSchema,
      // Dubai Payment From Office Account
      dubai_payment_from_office_account_payment_details:
        dubaiPaymentFromOfficePaymentDetailsSchema,
      dubai_payment_from_office_account_approver:
        dubaiPaymentFromOfficeApproverSchema,
      dubai_payment_from_office_account_payment_confirmation:
        dubaiPaymentFromOfficePaymentConfirmationSchema,
      // Dubai Transfer Requisition
      dubai_transfer_requisition_payment_details:
        dubaiTransferRequisitionPaymentDetailsSchema,
      dubai_transfer_requisition_first_partner:
        dubaiTransferRequisitionFirstPartnerApprovalSchema,
      dubai_transfer_requisition_second_partner:
        dubaiTransferRequisitionSecondPartnerApprovalSchema,
      dubai_transfer_requisition_payment_confirmation:
        dubaiTransferRequisitionPaymentConfirmationSchema,
      // Overtime Approval
      overtime_approval_request_details: overtimeApprovalRequestDetailsSchema,
      overtime_approval_line_manager: overtimeApprovalLineManagerSchema,
      overtime_approval_finance_confirmation:
        overtimeApprovalFinanceConfirmationSchema,
      gift_hospitality_donation_uk_request_details:
        giftHospitalityDonationRequestDetailsSchema,
      gift_hospitality_donation_uk_approval:
        giftHospitalityDonationApprovalSchema,
      gift_hospitality_donation_dubai_request_details:
        giftHospitalityDonationDubaiGiftHospitalityDonationDubaiRequestDetailsSchema,
      gift_hospitality_donation_dubai_approval:
        giftHospitalityDonationDubaiGiftHospitalityDonationDubaiApprovalSchema,
      gift_hospitality_donation_dublin_request_details:
        giftHospitalityDonationDublinGiftHospitalityDonationDublinRequestDetailsSchema,
      gift_hospitality_donation_dublin_approval:
        giftHospitalityDonationDublinGiftHospitalityDonationDublinApprovalSchema,
      // PLOP_INJECT_SCHEMA_MAP
    };
  }, [
    isFormReady,
    reassignmentSchema,
    paymentDetailsSchema,
    respLawyerSchema,
    firstPartnerSchema,
    secondPartnerSchema,
    paymentHoldReviewSchema,
    documentSavingSchema,
    paymentConfirmationSchema,
    dubaiPaymentFromOfficePaymentDetailsSchema,
    dubaiPaymentFromOfficeApproverSchema,
    dubaiTransferRequisitionPaymentDetailsSchema,
    dubaiTransferRequisitionFirstPartnerApprovalSchema,
    dubaiTransferRequisitionSecondPartnerApprovalSchema,
    dubaiPaymentFromOfficePaymentConfirmationSchema,
    dubaiTransferRequisitionPaymentConfirmationSchema,
    overtimeApprovalRequestDetailsSchema,
    overtimeApprovalLineManagerSchema,
    overtimeApprovalFinanceConfirmationSchema,
    giftHospitalityDonationRequestDetailsSchema,
    giftHospitalityDonationApprovalSchema,
    giftHospitalityDonationDubaiGiftHospitalityDonationDubaiRequestDetailsSchema,
    giftHospitalityDonationDubaiGiftHospitalityDonationDubaiApprovalSchema,
    giftHospitalityDonationDublinGiftHospitalityDonationDublinRequestDetailsSchema,
    giftHospitalityDonationDublinGiftHospitalityDonationDublinApprovalSchema,
    // PLOP_INJECT_SCHEMA_DEPENDENCIES
  ]);
};
